import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Landmark,
  Building2,
  DollarSign,
  Users,
  Search,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import {
  ProjectFundSummaryItem,
  useGetProjectFundSummaryQuery,
} from '../../store/slices/apiSlice';

const AdminProjectFunds: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed' | 'draft'>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isFetching, refetch } = useGetProjectFundSummaryQuery({
    search: debouncedSearch,
    status: statusFilter,
  });

  const summary = data?.data?.summary;
  const projects = data?.data?.projects ?? [];

  const formatCurrency = (amount: number, symbol: string = '$') => {
    return `${symbol}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)}`;
  };

  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all';

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const primaryCurrencySummary = summary?.by_currency?.[0];

  const columns = useMemo(() => [
    {
      key: 'title',
      label: 'Project',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{row.title}</p>
          <p className="text-xs text-gray-500 truncate">{row.location || 'No location'}</p>
          {row.category && (
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5">{row.category}</p>
          )}
        </div>
      ),
    },
    {
      key: 'amount_raised',
      label: 'Assigned Funds',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <div>
          <p className="font-bold text-green-700">{formatCurrency(row.amount_raised, row.currency_symbol)}</p>
          <p className="text-xs text-gray-500">
            Goal: {formatCurrency(row.funding_goal, row.currency_symbol)}
          </p>
        </div>
      ),
    },
    {
      key: 'approved_total',
      label: 'Approved Investments',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <div>
          <p className="font-semibold text-gray-900">{formatCurrency(row.approved_total, row.currency_symbol)}</p>
          <p className="text-xs text-gray-500">{row.approved_count} approved</p>
        </div>
      ),
    },
    {
      key: 'pending_total',
      label: 'Pending',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <div>
          <p className="font-semibold text-amber-700">{formatCurrency(row.pending_total, row.currency_symbol)}</p>
          <p className="text-xs text-gray-500">{row.pending_count} pending</p>
        </div>
      ),
    },
    {
      key: 'investor_count',
      label: 'Investors',
      render: (value: number) => (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
          <Users className="w-3.5 h-3.5" />
          {value}
        </span>
      ),
    },
    {
      key: 'funding_progress_pct',
      label: 'Progress',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <div className="min-w-[120px]">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-gray-700">{row.funding_progress_pct.toFixed(1)}%</span>
            {row.is_funded && <Badge variant="success">Funded</Badge>}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${row.is_funded ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min(100, row.funding_progress_pct)}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'is_reconciled',
      label: 'Reconciliation',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        row.approved_total === 0 && row.amount_raised === 0 ? (
          <span className="text-xs text-gray-400">No funds yet</span>
        ) : row.is_reconciled ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Matched
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            Mismatch
          </span>
        )
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: ProjectFundSummaryItem) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/investments')}
          className="text-xs"
        >
          <Eye className="w-3.5 h-3.5 mr-1" />
          Investments
        </Button>
      ),
    },
  ], [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Project Fund Treasury</h1>
          <p className="text-sm text-gray-600">
            Track and assign funds per project from the shared platform account
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="shrink-0"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 border-none shadow-lg shadow-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-blue-50 uppercase tracking-widest mb-1 opacity-80">Total Projects</p>
              <p className="text-3xl font-black text-white">{summary?.total_projects ?? 0}</p>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-emerald-500 to-emerald-600 border-none shadow-lg shadow-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-emerald-50 uppercase tracking-widest mb-1 opacity-80">Projects With Funds</p>
              <p className="text-3xl font-black text-white">{summary?.projects_with_funds ?? 0}</p>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl">
              <Landmark className="w-8 h-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 border-none shadow-lg shadow-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-purple-50 uppercase tracking-widest mb-1 opacity-80">Total Investors</p>
              <p className="text-3xl font-black text-white">{summary?.total_investors ?? 0}</p>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-amber-500 to-amber-600 border-none shadow-lg shadow-amber-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-black text-amber-50 uppercase tracking-widest mb-1 opacity-80">Needs Review</p>
              <p className="text-3xl font-black text-white">{summary?.unreconciled_projects ?? 0}</p>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {summary?.by_currency && summary.by_currency.length > 0 && (
        <Card className="p-6 border-none shadow-xl rounded-2xl">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Platform Fund Allocation by Currency</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            All funds land in one account — these totals show how much is assigned to each project bucket.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {summary.by_currency.map((currencySummary) => (
              <div
                key={currencySummary.currency_symbol}
                className="rounded-xl border border-gray-100 bg-gray-50/80 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-gray-900">
                    {currencySummary.currency_symbol} Projects
                  </span>
                  <Badge variant="default">{currencySummary.project_count} projects</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Assigned to projects</span>
                    <span className="font-bold text-green-700">
                      {formatCurrency(currencySummary.amount_raised_recorded, currencySummary.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Approved investments</span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(currencySummary.total_approved, currencySummary.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pending investments</span>
                    <span className="font-semibold text-amber-700">
                      {formatCurrency(currencySummary.total_pending, currencySummary.currency_symbol)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {primaryCurrencySummary && (
        <Card className="p-5 bg-blue-50 border border-blue-100 rounded-2xl">
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Single account, multiple project buckets</p>
              <p className="text-sm text-blue-800 mt-1">
                Use this page to see exactly how much of the shared treasury belongs to each project.
                Assigned funds ({formatCurrency(primaryCurrencySummary.amount_raised_recorded, primaryCurrencySummary.currency_symbol)})
                should match approved investments where reconciliation is green.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 mb-8 bg-white border-none shadow-xl rounded-2xl overflow-visible">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="relative flex-1 group min-w-[280px]">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <Input
                placeholder="Search by project title or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-gray-50/50 border-gray-200 focus:bg-white transition-all rounded-xl"
              />
            </div>
            <div className="flex items-center gap-4">
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="max-w-xs">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 mb-2 block">
              Project Status
            </label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'closed', label: 'Closed' },
                { value: 'draft', label: 'Draft' },
              ]}
              className="h-12 rounded-xl"
            />
          </div>
        </div>
      </Card>

      <Card className="border-none shadow-xl rounded-2xl overflow-hidden">
        <Table
          columns={columns}
          data={projects}
        />
      </Card>
    </div>
  );
};

export default AdminProjectFunds;
